import React, { useState, Fragment, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronRightIcon, UserIcon, HomeIcon, CalculatorIcon, FileTextIcon, CheckCircleIcon, PenToolIcon, SendIcon, DownloadIcon, LockIcon, PrinterIcon, EditIcon, EyeIcon } from 'lucide-react';
import { useRental, type Contract } from '../../contexts/RentalContext';
import { SignatureCanvas } from '../../components/rental/SignatureCanvas';
import { ContractPreviewModal } from '../../components/rental/ContractPreviewModal';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { generateContractPDF } from '../../utils/pdfGenerator';
type TransactionType = 'inchiriere' | 'vanzare';
type ClientRole = 'proprietar' | 'chirias';
type ClientType = 'PF' | 'PJ';
interface FormData {
  transactionType: TransactionType;
  clientRole: ClientRole;
  clientType: ClientType;
  // Client PF
  fullName: string;
  cnp: string;
  idSeries: string;
  idNumber: string;
  address: string;
  phone: string;
  email: string;
  // Client PJ
  companyName: string;
  cui: string;
  regCom: string;
  legalRep: string;
  companySediu: string;
  companyPhone: string;
  companyEmail: string;
  // Commission
  commissionPercent: string;
  commissionPaidBy: 'proprietar' | 'chirias' | 'ambele';
  vatOption: 'plus_vat' | 'vat_included';
  // Property
  propertyType: string;
  propertyAddress: string;
  rentalPrice: string;
  guarantee: string;
}
export function CreateContract() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    addContract,
    updateContract
  } = useRental();
  const [currentStep, setCurrentStep] = useState(1);
  const [agentSignature, setAgentSignature] = useState<string>('');
  const [clientSignature, setClientSignature] = useState<string>('');
  const [generatedContract, setGeneratedContract] = useState<Contract | null>(null);
  const [signingMode, setSigningMode] = useState<'physical' | 'digital' | null>(null);
  const [remoteLink, setRemoteLink] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    transactionType: 'inchiriere',
    clientRole: 'proprietar',
    clientType: 'PF',
    fullName: '',
    cnp: '',
    idSeries: '',
    idNumber: '',
    address: '',
    phone: '',
    email: '',
    companyName: '',
    cui: '',
    regCom: '',
    legalRep: '',
    companySediu: '',
    companyPhone: '',
    companyEmail: '',
    commissionPercent: '50',
    commissionPaidBy: 'proprietar',
    vatOption: 'vat_included',
    propertyType: 'apartament',
    propertyAddress: '',
    rentalPrice: '',
    guarantee: ''
  });
  const updateField = (field: keyof FormData, value: any) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      // Auto-set commissionPaidBy based on clientRole
      if (field === 'clientRole') {
        updated.commissionPaidBy = value; // 'proprietar' or 'chirias'
      }
      return updated;
    });
  };
  const steps = [{
    number: 1,
    title: 'Tip tranzacție',
    icon: FileTextIcon
  }, {
    number: 2,
    title: 'Date client',
    icon: UserIcon
  }, {
    number: 3,
    title: 'Comision',
    icon: CalculatorIcon
  }, {
    number: 4,
    title: 'Proprietate',
    icon: HomeIcon
  }, {
    number: 5,
    title: 'Semnare agent',
    icon: PenToolIcon
  }, {
    number: 6,
    title: 'Preview & Generare',
    icon: FileTextIcon
  }, {
    number: 7,
    title: 'Semnare beneficiar',
    icon: CheckCircleIcon
  }];
  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return formData.transactionType === 'inchiriere';
      case 2:
        if (formData.clientType === 'PF') {
          return !!(formData.fullName && formData.cnp && formData.idSeries && formData.idNumber && formData.address && formData.phone && formData.email);
        } else {
          return !!(formData.companyName && formData.cui && formData.regCom && formData.legalRep && formData.companySediu && formData.companyPhone && formData.companyEmail);
        }
      case 3:
        return !!(formData.commissionPercent && formData.commissionPaidBy);
      case 4:
        return !!(formData.propertyType && formData.propertyAddress);
      case 5:
        return !!agentSignature;
      default:
        return true;
    }
  };
  const nextStep = () => {
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    }
  };
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const getContractTemplate = () => {
    const today = new Date().toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const agentName = user?.fullName || 'Agent';
    // PROPRIETAR CONTRACT
    if (formData.clientRole === 'proprietar') {
      if (formData.clientType === 'PF') {
        return `CONTRACT DE INTERMEDIERE IMOBILIARĂ - PROPRIETAR PERSOANĂ FIZICĂ

Nr. ........../Data: ${today}

ÎNTRE:

1. FREIM ESTATE SRL, cu sediul social în București, Str. Mareșal Alexandru Averescu, nr. 15b-15c, Etaj 4, Sector 1, înregistrată la Registrul Comerțului sub nr. J40/4535/14.04.2020, având CUI 42451855, reprezentată în vederea semnării și executării prezentului contract de către ${agentName}, în calitate de INTERMEDIAR, pe de-o parte;

ȘI

2. ${formData.fullName}, domiciliat în ${formData.address}, identificat cu CI seria ${formData.idSeries} nr. ${formData.idNumber}, CNP ${formData.cnp}, telefon ${formData.phone}, e-mail ${formData.email}, în calitate de PROPRIETAR, pe de altă parte.

ART. 1 - OBIECTUL CONTRACTULUI

1.1. Obiectul prezentului contract îl constituie prestarea de către INTERMEDIAR a serviciilor de intermediere imobiliară pentru facilitarea închirierii imobilului proprietate a PROPRIETARULUI, situat la adresa: ${formData.propertyAddress}.

1.2. Tipul imobil: ${formData.propertyType}
${formData.rentalPrice ? `1.3. Preț chirie solicitat: ${formData.rentalPrice} EUR/lună` : ''}
${formData.guarantee ? `1.4. Garanție: ${formData.guarantee} EUR` : ''}

ART. 2 - DURATA CONTRACTULUI

2.1. Prezentul contract se încheie pe o perioadă de 6 luni de la data semnării.

2.2. Contractul încetează de drept la semnarea contractului de închiriere între PROPRIETAR și CHIRIAȘ identificat de INTERMEDIAR.

ART. 3 - OBLIGAȚIILE PĂRȚILOR

3.1. Obligațiile INTERMEDIARULUI:
• Să identifice și să prezinte PROPRIETARULUI potențiali chiriași;
• Să faciliteze vizionarea imobilului;
• Să asiste PROPRIETARUL în negocierea și semnarea contractului de închiriere;
• Să verifice bonitatea chiriașilor potențiali.

3.2. Obligațiile PROPRIETARULUI:
• Să furnizeze toate informațiile necesare despre imobil;
• Să permită accesul la vizionări în condiții rezonabile;
• Să achite comisionul convenit conform art. 4;
• Să nu contacteze direct chiriașii identificați de INTERMEDIAR fără acordul acestuia.

ART. 4 - COMISIONUL

4.1. Comisionul datorat de către PROPRIETAR către INTERMEDIAR este de ${formData.commissionPercent}% din valoarea primei luni de chirie ${formData.vatOption === 'vat_included' ? '(TVA inclus)' : '+ TVA (21%)'}.

4.2. Comisionul devine exigibil la momentul semnării contractului de închiriere între PROPRIETAR și CHIRIAȘ identificat de INTERMEDIAR.

4.3. Plata comisionului se va efectua în termen de 5 zile lucrătoare de la semnarea contractului de închiriere.

ART. 5 - CLAUZE SPECIALE

5.1. Orice tranzacție realizată prin informațiile și intervenția INTERMEDIARULUI atrage obligația de plată a comisionului.

5.2. Dacă PROPRIETARUL închiriază imobilul direct sau printr-un terț, pe baza informațiilor primite de la INTERMEDIAR, va datora comisionul stabilit.

ART. 6 - ÎNCETAREA CONTRACTULUI

6.1. Prezentul contract încetează prin:
• Semnarea contractului de închiriere între PROPRIETAR și CHIRIAȘ;
• Expirarea termenului de 6 luni;
• Acordul părților;
• Rezilierea unilaterală pentru neexecutarea obligațiilor contractuale.

ART. 7 - LITIGII

7.1. Litigiile născute din interpretarea sau executarea prezentului contract vor fi soluționate pe cale amiabilă, iar în caz contrar, de instanțele competente din București.

ART. 8 - DISPOZIȚII FINALE

8.1. Orice modificare a prezentului contract se face doar prin act adițional semnat de ambele părți.

8.2. Contractul a fost încheiat în două exemplare originale, câte unul pentru fiecare parte.

ART. 9 - PROTECȚIA DATELOR CU CHARACTER PERSONAL

9.1. Părțile declară că au luat cunoștință de prevederile Regulamentului (UE) 2016/679 privind protecția persoanelor fizice în ceea ce privește prelucrarea datelor cu caracter personal (GDPR).

9.2. INTERMEDIARUL, în calitate de operator de date, va prelucra datele personale ale PROPRIETARULUI strict în scopul executării prezentului contract.


SEMNĂTURI:

INTERMEDIAR:                                    PROPRIETAR:
FREIM ESTATE SRL                                ${formData.fullName}
Reprezentat de: ${agentName}

Semnătură: _________________                    Semnătură: _________________

Data: ${today}`;
      } else {
        // PROPRIETAR PJ
        return `CONTRACT DE INTERMEDIERE IMOBILIARĂ - PROPRIETAR PERSOANĂ JURIDICĂ

Nr. ........../Data: ${today}

ÎNTRE:

1. FREIM ESTATE SRL, cu sediul social în București, Str. Mareșal Alexandru Averescu, nr. 15b-15c, Etaj 4, Sector 1, înregistrată la Registrul Comerțului sub nr. J40/4535/14.04.2020, având CUI 42451855, reprezentată în vederea semnării și executării prezentului contract de către ${agentName}, în calitate de INTERMEDIAR, pe de-o parte;

ȘI

2. ${formData.companyName}, cu sediul social în ${formData.companySediu}, Nr. Reg. Com.: ${formData.regCom}, CUI: ${formData.cui}, reprezentată în vederea semnării și executării prezentului contract de ${formData.legalRep}, telefon ${formData.companyPhone}, e-mail ${formData.companyEmail}, în calitate de PROPRIETAR, pe de altă parte.

ART. 1 - OBIECTUL CONTRACTULUI

1.1. Obiectul prezentului contract îl constituie prestarea de către INTERMEDIAR a serviciilor de intermediere imobiliară pentru facilitarea închirierii imobilului proprietate a PROPRIETARULUI, situat la adresa: ${formData.propertyAddress}.

1.2. Tipul imobil: ${formData.propertyType}
${formData.rentalPrice ? `1.3. Preț chirie solicitat: ${formData.rentalPrice} EUR/lună` : ''}
${formData.guarantee ? `1.4. Garanție: ${formData.guarantee} EUR` : ''}

[... restul clauzelor identice cu PF ...]

SEMNĂTURI:

INTERMEDIAR:                                    PROPRIETAR:
FREIM ESTATE SRL                                ${formData.companyName}
Reprezentat de: ${agentName}                    Reprezentat de: ${formData.legalRep}

Semnătură: _________________                    Semnătură: _________________

Data: ${today}`;
      }
    } else {
      // CHIRIAȘ CONTRACT
      if (formData.clientType === 'PF') {
        return `CONTRACT DE INTERMEDIERE ÎN VEDEREA ÎNCHIRIERII - CHIRIAȘ PERSOANĂ FIZICĂ

ÎNCHEIAT ASTĂZI, ${today}

ÎNTRE:

1. FREIM ESTATE SRL, cu sediul social în București, Str. Mareșal Alexandru Averescu, nr. 15b-15c, Etaj 4, Sector 1, înregistrată la Registrul Comerțului sub nr. J40/4535/14.04.2020, având CUI 42451855, reprezentată în vederea semnării și executării prezentului contract de către ${agentName}, în calitate de INTERMEDIAR, pe de-o parte;

ȘI

2. ${formData.fullName}, domiciliat în ${formData.address}, identificat cu CI seria ${formData.idSeries} nr. ${formData.idNumber}, CNP ${formData.cnp}, telefon ${formData.phone}, e-mail ${formData.email}, denumit în continuare CHIRIAȘ, pe de altă parte.

ART. 1 - OBIECTUL CONTRACTULUI

1.1. Obiectul prezentului contract îl constituie prestarea de către INTERMEDIAR a serviciilor de intermediere imobiliară pentru facilitarea vizionării și eventual închirierii unui imobil identificat de INTERMEDIAR, care corespunde cerințelor exprimate de CHIRIAȘ.

1.2. Imobilul identificat este situat la adresa: ${formData.propertyAddress}

1.3. Tipul imobil: ${formData.propertyType}
${formData.rentalPrice ? `1.4. Preț chirie: ${formData.rentalPrice} EUR/lună` : ''}

ART. 2 - DURATA CONTRACTULUI

2.1. Prezentul contract se încheie pe o perioadă de 6 luni de la data semnării.

2.2. Contractul încetează de drept la semnarea contractului de închiriere între CHIRIAȘ și PROPRIETAR pentru imobilul vizionat.

ART. 3 - OBLIGAȚIILE PĂRȚILOR

3.1. Obligațiile INTERMEDIARULUI:
• Să faciliteze accesul CHIRIAȘULUI la vizionarea imobilului identificat;
• Să asiste CHIRIAȘUL în negocierea și semnarea contractului de închiriere;
• Să verifice conformitatea imobilului cu cerințele CHIRIAȘULUI.

3.2. Obligațiile CHIRIAȘULUI:
• Să participe la vizionarea imobilului;
• Să achite comisionul convenit conform art. 4;
• Să nu contacteze direct PROPRIETARUL fără acordul și implicarea INTERMEDIARULUI.

ART. 4 - COMISIONUL

4.1. Comisionul datorat de către CHIRIAȘ către INTERMEDIAR este de ${formData.commissionPercent}% din valoarea primei luni de chirie ${formData.vatOption === 'vat_included' ? '(TVA inclus)' : '+ TVA (21%)'}.

4.2. Comisionul devine exigibil doar la momentul semnării contractului de închiriere între CHIRIAȘ și PROPRIETAR.

4.3. Plata comisionului se va efectua în termen de 5 zile lucrătoare de la semnarea contractului de închiriere.

ART. 5 - CLAUZE SPECIALE

5.1. Orice tranzacție realizată prin informațiile și intervenția INTERMEDIARULUI atrage obligația de plată a comisionului.

5.2. Dacă CHIRIAȘUL închiriază imobilul direct sau printr-un terț, pe baza informațiilor primite de la INTERMEDIAR, va datora comisionul stabilit.

ART. 6 - ÎNCETAREA CONTRACTULUI

6.1. Prezentul contract încetează prin:
• Semnarea contractului de închiriere între CHIRIAȘ și PROPRIETAR;
• Expirarea termenului de 6 luni;
• Acordul părților;
• Rezilierea unilaterală pentru neexecutarea obligațiilor contractuale.

ART. 7 - LITIGII

7.1. Litigiile născute din interpretarea sau executarea prezentului contract vor fi soluționate pe cale amiabilă, iar în caz contrar, de instanțele competente din București.

ART. 8 - DISPOZIȚII FINALE

8.1. Orice modificare a prezentului contract se face doar prin act adițional semnat de ambele părți.

8.2. Contractul a fost încheiat în două exemplare originale, câte unul pentru fiecare parte.

ART. 9 - PROTECȚIA DATELOR CU CHARACTER PERSONAL

9.1. Părțile declară că au luat cunoștință de prevederile Regulamentului (UE) 2016/679 privind protecția persoanelor fizice în ceea ce privește prelucrarea datelor cu caracter personal (GDPR).


SEMNĂTURI:

INTERMEDIAR:                                    CHIRIAȘ:
FREIM ESTATE SRL                                ${formData.fullName}
Reprezentat de: ${agentName}

Semnătură: _________________                    Semnătură: _________________

Data: ${today}`;
      } else {
        // CHIRIAȘ PJ
        return `CONTRACT DE INTERMEDIERE ÎN VEDEREA ÎNCHIRIERII - CHIRIAȘ PERSOANĂ JURIDICĂ

ÎNCHEIAT ASTĂZI, ${today}

ÎNTRE:

1. FREIM ESTATE SRL, cu sediul social în București, Str. Mareșal Alexandru Averescu, nr. 15b-15c, Etaj 4, Sector 1, înregistrată la Registrul Comerțului sub nr. J40/4535/14.04.2020, având CUI 42451855, reprezentată în vederea semnării și executării prezentului contract de către ${agentName}, în calitate de INTERMEDIAR, pe de-o parte;

ȘI

2. ${formData.companyName}, cu sediul social în ${formData.companySediu}, Nr. Reg. Com.: ${formData.regCom}, CUI: ${formData.cui}, reprezentată în vederea semnării și executării prezentului contract de ${formData.legalRep}, telefon ${formData.companyPhone}, e-mail ${formData.companyEmail}, denumit în continuare CHIRIAȘ, pe de altă parte.

[... restul clauzelor identice cu Chiriaș PF ...]

SEMNĂTURI:

INTERMEDIAR:                                    CHIRIAȘ:
FREIM ESTATE SRL                                ${formData.companyName}
Reprezentat de: ${agentName}                    Reprezentat de: ${formData.legalRep}

Semnătură: _________________                    Semnătură: _________________

Data: ${today}`;
      }
    }
  };
  const handleGenerateContract = () => {
    const contract: Contract = {
      id: `CNT-${Date.now()}`,
      type: formData.clientRole === 'proprietar' ? 'landlord' : 'tenant',
      clientType: formData.clientType,
      status: 'draft',
      createdAt: new Date().toISOString(),
      clientName: formData.clientType === 'PF' ? formData.fullName : formData.companyName,
      clientPhone: formData.clientType === 'PF' ? formData.phone : formData.companyPhone,
      clientEmail: formData.clientType === 'PF' ? formData.email : formData.companyEmail,
      clientCNP: formData.clientType === 'PF' ? formData.cnp : undefined,
      clientCI: formData.clientType === 'PF' ? `${formData.idSeries} ${formData.idNumber}` : undefined,
      clientAddress: formData.clientType === 'PF' ? formData.address : undefined,
      clientCUI: formData.clientType === 'PJ' ? formData.cui : undefined,
      clientCompanyName: formData.clientType === 'PJ' ? formData.companyName : undefined,
      clientCompanyAddress: formData.clientType === 'PJ' ? formData.companySediu : undefined,
      clientRegCom: formData.clientType === 'PJ' ? formData.regCom : undefined,
      clientLegalRep: formData.clientType === 'PJ' ? formData.legalRep : undefined,
      propertyAddress: formData.propertyAddress,
      propertyType: formData.propertyType,
      rentalPrice: parseFloat(formData.rentalPrice) || 0,
      commissionPercent: parseFloat(formData.commissionPercent) || 0,
      vatIncluded: formData.vatOption === 'vat_included',
      monthlyRent: parseFloat(formData.rentalPrice) || 0,
      commissionAmount: 0,
      vatAmount: 0,
      totalAmount: 0,
      agentSignature
    };
    addContract(contract);
    setGeneratedContract(contract);
    setIsEditMode(false);
    toast.success('Contract generat cu succes!');
    nextStep();
  };
  const handleEditContract = (step: number) => {
    setIsEditMode(true);
    setCurrentStep(step);
    toast.info('Poți edita datele contractului');
  };
  const handleSaveEdits = () => {
    if (generatedContract) {
      // Update the generated contract with new data
      const updatedContract: Contract = {
        ...generatedContract,
        clientName: formData.clientType === 'PF' ? formData.fullName : formData.companyName,
        clientPhone: formData.clientType === 'PF' ? formData.phone : formData.companyPhone,
        clientEmail: formData.clientType === 'PF' ? formData.email : formData.companyEmail,
        clientCNP: formData.clientType === 'PF' ? formData.cnp : undefined,
        clientCI: formData.clientType === 'PF' ? `${formData.idSeries} ${formData.idNumber}` : undefined,
        clientAddress: formData.clientType === 'PF' ? formData.address : undefined,
        clientCUI: formData.clientType === 'PJ' ? formData.cui : undefined,
        clientCompanyName: formData.clientType === 'PJ' ? formData.companyName : undefined,
        clientCompanyAddress: formData.clientType === 'PJ' ? formData.companySediu : undefined,
        clientRegCom: formData.clientType === 'PJ' ? formData.regCom : undefined,
        clientLegalRep: formData.clientType === 'PJ' ? formData.legalRep : undefined,
        propertyAddress: formData.propertyAddress,
        propertyType: formData.propertyType,
        rentalPrice: parseFloat(formData.rentalPrice) || 0,
        commissionPercent: parseFloat(formData.commissionPercent) || 0,
        vatIncluded: formData.vatOption === 'vat_included',
        monthlyRent: parseFloat(formData.rentalPrice) || 0
      };
      updateContract(generatedContract.id, updatedContract);
      setGeneratedContract(updatedContract);
      setIsEditMode(false);
      setCurrentStep(6);
      toast.success('Modificări salvate cu succes!');
    }
  };
  const handlePhysicalSigning = () => {
    if (generatedContract) {
      updateContract(generatedContract.id, {
        status: 'printed'
      });
      toast.success('Contract pregătit pentru semnare fizică!');
      setTimeout(() => navigate('/inchiriere/contracte'), 1500);
    }
  };
  const generateRemoteLink = () => {
    const uniqueId = `${generatedContract?.id}-${Date.now()}`;
    const link = `${window.location.origin}/sign/${uniqueId}`;
    setRemoteLink(link);
    return link;
  };
  const handleSendWhatsApp = () => {
    const link = generateRemoteLink();
    if (generatedContract) {
      const message = encodeURIComponent(`Bună ziua,\n\nVă rugăm să semnați contractul accesând linkul:\n${link}\n\nMulțumim!`);
      window.open(`https://wa.me/${generatedContract.clientPhone.replace(/\D/g, '')}?text=${message}`, '_blank');
      updateContract(generatedContract.id, {
        status: 'sent',
        signingLink: link
      });
      toast.success('Link trimis pe WhatsApp!');
      setTimeout(() => navigate('/inchiriere/contracte'), 1500);
    }
  };
  const handleDownloadPDF = async () => {
    if (generatedContract && user) {
      try {
        // Create a temporary div with the contract content
        const contractContent = document.createElement('div');
        contractContent.style.cssText = 'position: absolute; left: -9999px; width: 210mm; padding: 20mm; font-family: serif; font-size: 12pt; line-height: 1.6;';
        contractContent.innerHTML = `
          <div style="text-align: center; margin-bottom: 30px;">
            <h1 style="font-size: 18pt; font-weight: bold; margin-bottom: 10px;">
              CONTRACT DE INTERMEDIERE IMOBILIARĂ
            </h1>
            <h2 style="font-size: 14pt; font-weight: bold;">
              ${generatedContract.type === 'landlord' ? 'PROPRIETAR' : 'CHIRIAȘ'} 
              ${generatedContract.clientType === 'PF' ? 'PERSOANĂ FIZICĂ' : 'PERSOANĂ JURIDICĂ'}
            </h2>
          </div>
          
          <div style="margin-bottom: 20px;">
            <p><strong>Nr. .......... / Data:</strong> ${new Date(generatedContract.createdAt).toLocaleDateString('ro-RO')}</p>
          </div>
          
          <div style="margin-bottom: 20px;">
            <p style="font-weight: bold; margin-bottom: 10px;">ÎNTRE:</p>
            <p style="margin-bottom: 10px;">
              1. FREIM ESTATE SRL, cu sediul social în București, Str. Mareșal Alexandru Averescu, nr. 15b-15c, Etaj 4, Sector 1, 
              înregistrată la Registrul Comerțului sub nr. J40/4535/14.04.2020, având CUI 42451855, 
              reprezentată în vederea semnării și executării prezentului contract de către ${user.fullName || 'Agent'}, 
              în calitate de INTERMEDIAR, pe de-o parte;
            </p>
            <p style="text-align: center; margin: 10px 0;">ȘI</p>
            <p>
              2. ${generatedContract.clientType === 'PF' ? `${generatedContract.clientName}, domiciliat în ${generatedContract.clientAddress}, identificat cu CI seria ${generatedContract.clientCI}, CNP ${generatedContract.clientCNP}, telefon ${generatedContract.clientPhone}, e-mail ${generatedContract.clientEmail}` : `${generatedContract.clientCompanyName}, cu sediul social în ${generatedContract.clientCompanyAddress}, Nr. Reg. Com.: ${generatedContract.clientRegCom}, CUI: ${generatedContract.clientCUI}, reprezentată de ${generatedContract.clientLegalRep}, telefon ${generatedContract.clientPhone}, e-mail ${generatedContract.clientEmail}`}, 
              în calitate de ${generatedContract.type === 'landlord' ? 'PROPRIETAR' : 'CHIRIAȘ'}, pe de altă parte.
            </p>
          </div>
          
          <div style="margin-bottom: 20px;">
            <p style="font-weight: bold; margin-bottom: 10px;">ART. 1 - OBIECTUL CONTRACTULUI</p>
            <p style="margin-bottom: 10px;">
              1.1. Obiectul prezentului contract îl constituie prestarea de către INTERMEDIAR a serviciilor de intermediere imobiliară 
              pentru facilitarea ${generatedContract.type === 'landlord' ? 'închirierii imobilului proprietate a PROPRIETARULUI' : 'vizionării și eventual închirierii unui imobil identificat de INTERMEDIAR'}, 
              situat la adresa: ${generatedContract.propertyAddress}.
            </p>
            <p style="margin-bottom: 10px;">1.2. Tipul imobil: ${generatedContract.propertyType}</p>
            ${generatedContract.rentalPrice > 0 ? `<p>1.3. Preț chirie: ${generatedContract.rentalPrice} EUR/lună</p>` : ''}
          </div>
          
          <div style="margin-bottom: 20px;">
            <p style="font-weight: bold; margin-bottom: 10px;">ART. 2 - COMISIONUL</p>
            <p>
              Comisionul datorat de către ${generatedContract.type === 'landlord' ? 'PROPRIETAR' : 'CHIRIAȘ'} către INTERMEDIAR 
              este de ${generatedContract.commissionPercent}% din valoarea primei luni de chirie 
              ${generatedContract.vatIncluded ? '(TVA inclus)' : '+ TVA (21%)'}.
            </p>
          </div>
          
          <div style="margin-top: 50px;">
            <p style="font-weight: bold; margin-bottom: 20px;">SEMNĂTURI:</p>
            <div style="display: flex; justify-content: space-between;">
              <div style="width: 45%;">
                <p style="font-weight: bold;">INTERMEDIAR:</p>
                <p>FREIM ESTATE SRL</p>
                <p>Reprezentat de: ${user.fullName || 'Agent'}</p>
                ${generatedContract.agentSignature ? `<img src="${generatedContract.agentSignature}" style="width: 150px; height: 60px; margin-top: 10px;" />` : '<p style="margin-top: 20px;">_________________</p>'}
              </div>
              <div style="width: 45%;">
                <p style="font-weight: bold;">${generatedContract.type === 'landlord' ? 'PROPRIETAR' : 'CHIRIAȘ'}:</p>
                <p>${generatedContract.clientName}</p>
                ${generatedContract.clientType === 'PJ' ? `<p>Reprezentat de: ${generatedContract.clientLegalRep}</p>` : ''}
                ${generatedContract.clientSignature ? `<img src="${generatedContract.clientSignature}" style="width: 150px; height: 60px; margin-top: 10px;" />` : '<p style="margin-top: 20px;">_________________</p>'}
              </div>
            </div>
            <p style="margin-top: 20px;">Data: ${new Date(generatedContract.createdAt).toLocaleDateString('ro-RO')}</p>
          </div>
        `;
        document.body.appendChild(contractContent);
        // Use window.print() for now - browser will handle PDF generation
        window.print();
        // Clean up
        document.body.removeChild(contractContent);
        toast.success('Contract pregătit pentru descărcare!');
        updateContract(generatedContract.id, {
          status: 'printed'
        });
      } catch (error) {
        console.error('Eroare la generarea PDF:', error);
        toast.error('Eroare la descărcarea contractului');
      }
    }
  };
  return <div className="min-h-screen bg-slate-50 pb-20" data-id="element-9295">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white pt-10 pb-16 px-4 sm:px-6 lg:px-8" data-id="element-9296">
        <div className="max-w-7xl mx-auto" data-id="element-9297">
          <button onClick={() => navigate('/inchiriere/contracte')} className="flex items-center space-x-2 text-white hover:text-blue-200 transition-colors mb-6" data-id="element-9298">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-9299" />
            <span data-id="element-9300">Înapoi la contracte</span>
          </button>

          <h1 className="text-3xl font-bold mb-2" data-id="element-9301">Contract Nou - Închiriere</h1>
          <p className="text-blue-200" data-id="element-9302">
            Pasul {currentStep} din {steps.length}:{' '}
            {steps[currentStep - 1].title}
          </p>

          {/* Progress Bar */}
          <div className="mt-6 flex items-center space-x-2" data-id="element-9303">
            {steps.map((step, index) => <div key={step.number} className={`flex-1 h-2 rounded-full transition-all ${currentStep > step.number ? 'bg-[#21A366]' : currentStep === step.number ? 'bg-[#357BEB]' : 'bg-white bg-opacity-20'}`} data-id="element-9304" />)}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8" data-id="element-9305">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8" data-id="element-9306">
          <AnimatePresence mode="wait" data-id="element-9307">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-9308">
              {/* STEP 1 - Transaction Type */}
              {currentStep === 1 && <div className="space-y-6" data-id="element-9309">
                  <h3 className="text-2xl font-bold text-[#132C53] mb-6" data-id="element-9310">
                    Alege tip tranzacție
                  </h3>

                  <div className="grid grid-cols-2 gap-6" data-id="element-9311">
                    <button type="button" onClick={() => updateField('transactionType', 'inchiriere')} className={`p-8 rounded-xl border-2 font-semibold transition-all ${formData.transactionType === 'inchiriere' ? 'border-[#357BEB] bg-[#357BEB] text-white shadow-lg' : 'border-gray-300 bg-white text-gray-700 hover:border-[#357BEB]'}`} data-id="element-9312">
                      <FileTextIcon className="w-12 h-12 mx-auto mb-3" data-id="element-9313" />
                      <span className="text-xl" data-id="element-9314">Închiriere</span>
                    </button>

                    <div className="relative group" data-id="element-9315">
                      <button type="button" disabled className="w-full p-8 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-400 font-semibold cursor-not-allowed opacity-50" data-id="element-9316">
                        <div className="relative" data-id="element-9317">
                          <FileTextIcon className="w-12 h-12 mx-auto mb-3" data-id="element-9318" />
                          <LockIcon className="w-5 h-5 absolute top-0 right-1/2 translate-x-8 -translate-y-1 text-gray-400" data-id="element-9319" />
                        </div>
                        <span className="text-xl" data-id="element-9320">Vânzare</span>
                        <p className="text-sm mt-2" data-id="element-9321">Blocat</p>
                      </button>

                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-3 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-64 text-center z-10" data-id="element-9322">
                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-9323"></div>
                        Contractele de vânzare se deblochează după prima
                        tranzacție de închiriere finalizată.
                      </div>
                    </div>
                  </div>
                </div>}

              {/* STEP 2 - Client Data - SAME AS BEFORE */}
              {currentStep === 2 && <div className="space-y-6" data-id="element-9324">
                  <h3 className="text-2xl font-bold text-[#132C53] mb-6" data-id="element-9325">
                    Pentru cine creezi contractul?
                  </h3>

                  {/* Client Role Selection */}
                  <div data-id="element-9326">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-9327">
                      Tip client *
                    </label>
                    <div className="grid grid-cols-2 gap-4" data-id="element-9328">
                      <button type="button" onClick={() => updateField('clientRole', 'proprietar')} className={`p-4 rounded-lg border-2 font-semibold transition-all ${formData.clientRole === 'proprietar' ? 'border-[#357BEB] bg-[#357BEB] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#357BEB]'}`} data-id="element-9329">
                        Proprietar
                      </button>
                      <button type="button" onClick={() => updateField('clientRole', 'chirias')} className={`p-4 rounded-lg border-2 font-semibold transition-all ${formData.clientRole === 'chirias' ? 'border-[#357BEB] bg-[#357BEB] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#357BEB]'}`} data-id="element-9330">
                        Chiriaș
                      </button>
                    </div>
                  </div>

                  {/* Person Type Selection */}
                  <div data-id="element-9331">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-9332">
                      Tip persoană *
                    </label>
                    <div className="grid grid-cols-2 gap-4" data-id="element-9333">
                      <button type="button" onClick={() => updateField('clientType', 'PF')} className={`p-4 rounded-lg border-2 font-semibold transition-all ${formData.clientType === 'PF' ? 'border-[#357BEB] bg-[#357BEB] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#357BEB]'}`} data-id="element-9334">
                        Persoană Fizică
                      </button>
                      <button type="button" onClick={() => updateField('clientType', 'PJ')} className={`p-4 rounded-lg border-2 font-semibold transition-all ${formData.clientType === 'PJ' ? 'border-[#357BEB] bg-[#357BEB] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#357BEB]'}`} data-id="element-9335">
                        Persoană Juridică
                      </button>
                    </div>
                  </div>

                  {/* PF Form */}
                  {formData.clientType === 'PF' && <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-9336">
                      <h4 className="font-bold text-gray-900" data-id="element-9337">
                        Date Persoană Fizică
                      </h4>

                      <div data-id="element-9338">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9339">
                          Nume complet *
                        </label>
                        <input type="text" value={formData.fullName} onChange={e => updateField('fullName', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="Popescu Ion" data-id="element-9340" />
                      </div>

                      <div className="grid grid-cols-3 gap-4" data-id="element-9341">
                        <div data-id="element-9342">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9343">
                            Serie CI *
                          </label>
                          <input type="text" value={formData.idSeries} onChange={e => updateField('idSeries', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="AB" data-id="element-9344" />
                        </div>

                        <div data-id="element-9345">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9346">
                            Nr. CI *
                          </label>
                          <input type="text" value={formData.idNumber} onChange={e => updateField('idNumber', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="123456" data-id="element-9347" />
                        </div>

                        <div data-id="element-9348">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9349">
                            CNP *
                          </label>
                          <input type="text" value={formData.cnp} onChange={e => updateField('cnp', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="1234567890123" maxLength={13} data-id="element-9350" />
                        </div>
                      </div>

                      <div data-id="element-9351">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9352">
                          Adresă domiciliu *
                        </label>
                        <input type="text" value={formData.address} onChange={e => updateField('address', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="Str. Exemplu, Nr. 1, Sector 1, București" data-id="element-9353" />
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-9354">
                        <div data-id="element-9355">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9356">
                            Telefon *
                          </label>
                          <input type="tel" value={formData.phone} onChange={e => updateField('phone', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="0721234567" data-id="element-9357" />
                        </div>

                        <div data-id="element-9358">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9359">
                            Email *
                          </label>
                          <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="email@example.com" data-id="element-9360" />
                        </div>
                      </div>
                    </div>}

                  {/* PJ Form */}
                  {formData.clientType === 'PJ' && <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-9361">
                      <h4 className="font-bold text-gray-900" data-id="element-9362">
                        Date Persoană Juridică
                      </h4>

                      <div data-id="element-9363">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9364">
                          Denumire firmă *
                        </label>
                        <input type="text" value={formData.companyName} onChange={e => updateField('companyName', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="SC EXEMPLU SRL" data-id="element-9365" />
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-9366">
                        <div data-id="element-9367">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9368">
                            CUI *
                          </label>
                          <input type="text" value={formData.cui} onChange={e => updateField('cui', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="RO12345678" data-id="element-9369" />
                        </div>

                        <div data-id="element-9370">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9371">
                            Nr. Reg. Com. *
                          </label>
                          <input type="text" value={formData.regCom} onChange={e => updateField('regCom', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="J40/1234/2020" data-id="element-9372" />
                        </div>
                      </div>

                      <div data-id="element-9373">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9374">
                          Reprezentant legal *
                        </label>
                        <input type="text" value={formData.legalRep} onChange={e => updateField('legalRep', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="Popescu Ion" data-id="element-9375" />
                      </div>

                      <div data-id="element-9376">
                        <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9377">
                          Sediu social *
                        </label>
                        <input type="text" value={formData.companySediu} onChange={e => updateField('companySediu', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="Str. Exemplu, Nr. 1, Sector 1, București" data-id="element-9378" />
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-9379">
                        <div data-id="element-9380">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9381">
                            Telefon *
                          </label>
                          <input type="tel" value={formData.companyPhone} onChange={e => updateField('companyPhone', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="0721234567" data-id="element-9382" />
                        </div>

                        <div data-id="element-9383">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9384">
                            Email *
                          </label>
                          <input type="email" value={formData.companyEmail} onChange={e => updateField('companyEmail', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="contact@firma.ro" data-id="element-9385" />
                        </div>
                      </div>
                    </div>}
                </div>}

              {/* STEP 3 - Commission */}
              {currentStep === 3 && <div className="space-y-6" data-id="element-9386">
                  <h3 className="text-2xl font-bold text-[#132C53] mb-6" data-id="element-9387">
                    Adaugă comisionare
                  </h3>

                  <div data-id="element-9388">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9389">
                      Procent comision (%) *
                    </label>
                    <input type="number" value={formData.commissionPercent} onChange={e => updateField('commissionPercent', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="50" min="0" max="100" data-id="element-9390" />
                  </div>

                  <div data-id="element-9391">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-9392">
                      Opțiune TVA *
                    </label>
                    <div className="grid grid-cols-2 gap-4" data-id="element-9393">
                      <button type="button" onClick={() => updateField('vatOption', 'vat_included')} className={`p-4 rounded-lg border-2 font-semibold transition-all ${formData.vatOption === 'vat_included' ? 'border-[#357BEB] bg-[#357BEB] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#357BEB]'}`} data-id="element-9394">
                        TVA inclus
                      </button>
                      <button type="button" onClick={() => updateField('vatOption', 'plus_vat')} className={`p-4 rounded-lg border-2 font-semibold transition-all ${formData.vatOption === 'plus_vat' ? 'border-[#357BEB] bg-[#357BEB] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#357BEB]'}`} data-id="element-9395">
                        + TVA (21%)
                      </button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-xl p-6" data-id="element-9396">
                    <h4 className="font-bold text-gray-900 mb-3" data-id="element-9397">
                      Comision configurat:
                    </h4>
                    <p className="text-2xl font-bold text-green-700" data-id="element-9398">
                      {formData.commissionPercent}%{' '}
                      {formData.vatOption === 'vat_included' ? '(TVA inclus)' : '+ TVA'}
                    </p>
                    <p className="text-sm text-gray-600 mt-2" data-id="element-9399">
                      Plătit de:{' '}
                      {formData.clientRole === 'proprietar' ? 'Proprietar' : 'Chiriaș'}
                    </p>
                  </div>
                </div>}

              {/* STEP 4 - Property */}
              {currentStep === 4 && <div className="space-y-6" data-id="element-9400">
                  <h3 className="text-2xl font-bold text-[#132C53] mb-6" data-id="element-9401">
                    Adaugă proprietate
                  </h3>

                  <div data-id="element-9402">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9403">
                      Tip imobil *
                    </label>
                    <select value={formData.propertyType} onChange={e => updateField('propertyType', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" data-id="element-9404">
                      <option value="apartament" data-id="element-9405">Apartament</option>
                      <option value="casa" data-id="element-9406">Casă</option>
                      <option value="vila" data-id="element-9407">Vilă</option>
                      <option value="spatiu_comercial" data-id="element-9408">Spațiu comercial</option>
                      <option value="birouri" data-id="element-9409">Birouri</option>
                    </select>
                  </div>

                  <div data-id="element-9410">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9411">
                      Adresă exactă *
                    </label>
                    <input type="text" value={formData.propertyAddress} onChange={e => updateField('propertyAddress', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="Str. Exemplu, Nr. 1, Bl. A, Sc. B, Et. 3, Ap. 15, Sector 1, București" data-id="element-9412" />
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-9413">
                    <div data-id="element-9414">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9415">
                        Preț solicitat (EUR)
                      </label>
                      <input type="number" value={formData.rentalPrice} onChange={e => updateField('rentalPrice', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="450" data-id="element-9416" />
                    </div>

                    <div data-id="element-9417">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-9418">
                        Garanție (EUR) - opțional
                      </label>
                      <input type="number" value={formData.guarantee} onChange={e => updateField('guarantee', e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#357BEB]" placeholder="450" data-id="element-9419" />
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9420">
                    <p className="text-sm text-gray-700" data-id="element-9421">
                      <strong data-id="element-9422">✓ Detalii complete</strong> - Toate informațiile
                      necesare au fost completate.
                    </p>
                  </div>
                </div>}

              {/* STEP 5 - Agent Signature */}
              {currentStep === 5 && <div className="space-y-6" data-id="element-9423">
                  <h3 className="text-2xl font-bold text-[#132C53] mb-6" data-id="element-9424">
                    Semnare agent
                  </h3>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-9425">
                    <p className="text-sm text-gray-700" data-id="element-9426">
                      <strong data-id="element-9427">Important:</strong> Agentul trebuie să semneze
                      contractul înainte de generare. Semnătura ta confirmă că
                      toate datele sunt corecte.
                    </p>
                  </div>

                  <div data-id="element-9428">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-9429">
                      Semnătură agent *
                    </label>
                    <SignatureCanvas onSave={setAgentSignature} data-id="element-9430" />
                  </div>

                  {agentSignature && <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-9431">
                      <div className="flex items-center space-x-2 mb-2" data-id="element-9432">
                        <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-9433" />
                        <p className="text-sm font-semibold text-gray-900" data-id="element-9434">
                          Semnătură salvată
                        </p>
                      </div>
                      <div className="border border-gray-200 rounded-lg p-2 bg-white mt-2" data-id="element-9435">
                        <img src={agentSignature} alt="Agent Signature" className="w-48 h-24 object-contain mx-auto" data-id="element-9436" />
                      </div>
                    </div>}
                </div>}

              {/* STEP 6 - Preview & Generate */}
              {currentStep === 6 && <div className="space-y-6" data-id="element-9437">
                  <div className="flex items-center justify-between mb-6" data-id="element-9438">
                    <h3 className="text-2xl font-bold text-[#132C53]" data-id="element-9439">
                      {generatedContract ? 'Contract Generat' : 'Preview & Generare Contract'}
                    </h3>

                    <button type="button" onClick={() => setIsPreviewModalOpen(true)} className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-colors" data-id="element-9440">
                      <EyeIcon className="w-4 h-4" data-id="element-9441" />
                      <span data-id="element-9442">Vezi Contractul Complet</span>
                    </button>
                  </div>

                  {/* Summary Card */}
                  <div className="bg-gradient-to-br from-[#357BEB] to-[#2868d8] rounded-xl p-6 text-white mb-6" data-id="element-9443">
                    <div className="flex items-start justify-between mb-4" data-id="element-9444">
                      <div className="flex-1" data-id="element-9445">
                        <h4 className="text-xl font-bold mb-2" data-id="element-9446">
                          Contract{' '}
                          {formData.clientRole === 'proprietar' ? 'Proprietar' : 'Chiriaș'}{' '}
                          -{' '}
                          {formData.clientType === 'PF' ? 'Persoană Fizică' : 'Persoană Juridică'}
                        </h4>
                        <p className="text-white text-opacity-90 text-lg font-semibold" data-id="element-9447">
                          {formData.clientType === 'PF' ? formData.fullName : formData.companyName}
                        </p>
                      </div>

                      {generatedContract && <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs font-bold" data-id="element-9448">
                          {generatedContract.status === 'draft' ? 'Draft' : 'Generat'}
                        </span>}
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-9449">
                      <div data-id="element-9450">
                        <p className="text-white text-opacity-70 mb-1" data-id="element-9451">
                          Proprietate:
                        </p>
                        <p className="font-semibold" data-id="element-9452">
                          {formData.propertyAddress}
                        </p>
                      </div>
                      <div data-id="element-9453">
                        <p className="text-white text-opacity-70 mb-1" data-id="element-9454">
                          Comision:
                        </p>
                        <p className="text-lg font-bold" data-id="element-9455">
                          {formData.commissionPercent}%{' '}
                          {formData.vatOption === 'vat_included' ? '(TVA inclus)' : '+ TVA'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Edit Buttons - Show only after contract is generated */}
                  {generatedContract && <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-6" data-id="element-9456">
                      <h4 className="font-bold text-gray-900 mb-3 flex items-center space-x-2" data-id="element-9457">
                        <EditIcon className="w-5 h-5 text-amber-600" data-id="element-9458" />
                        <span data-id="element-9459">Editează Date Contract</span>
                      </h4>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-9460">
                        Dacă ai observat o greșeală, poți edita datele
                        contractului înainte de semnare.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-9461">
                        <button type="button" onClick={() => handleEditContract(2)} className="flex items-center justify-center space-x-2 px-4 py-2 bg-white border-2 border-amber-300 text-amber-700 rounded-lg font-semibold hover:bg-amber-50 transition-colors" data-id="element-9462">
                          <UserIcon className="w-4 h-4" data-id="element-9463" />
                          <span data-id="element-9464">Date Client</span>
                        </button>
                        <button type="button" onClick={() => handleEditContract(3)} className="flex items-center justify-center space-x-2 px-4 py-2 bg-white border-2 border-amber-300 text-amber-700 rounded-lg font-semibold hover:bg-amber-50 transition-colors" data-id="element-9465">
                          <CalculatorIcon className="w-4 h-4" data-id="element-9466" />
                          <span data-id="element-9467">Comision</span>
                        </button>
                        <button type="button" onClick={() => handleEditContract(4)} className="flex items-center justify-center space-x-2 px-4 py-2 bg-white border-2 border-amber-300 text-amber-700 rounded-lg font-semibold hover:bg-amber-50 transition-colors" data-id="element-9468">
                          <HomeIcon className="w-4 h-4" data-id="element-9469" />
                          <span data-id="element-9470">Proprietate</span>
                        </button>
                        <button type="button" onClick={() => handleEditContract(5)} className="flex items-center justify-center space-x-2 px-4 py-2 bg-white border-2 border-amber-300 text-amber-700 rounded-lg font-semibold hover:bg-amber-50 transition-colors" data-id="element-9471">
                          <PenToolIcon className="w-4 h-4" data-id="element-9472" />
                          <span data-id="element-9473">Semnătură</span>
                        </button>
                      </div>
                    </div>}

                  {/* Info Box */}
                  {!generatedContract && <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6" data-id="element-9474">
                      <h4 className="font-bold text-gray-900 mb-3" data-id="element-9475">
                        📋 Ce se întâmplă după generare:
                      </h4>
                      <ul className="space-y-2 text-sm text-gray-700" data-id="element-9476">
                        <li className="flex items-start space-x-2" data-id="element-9477">
                          <span className="text-[#21A366] font-bold" data-id="element-9478">✓</span>
                          <span data-id="element-9479">
                            Contractul va fi generat cu toate datele completate
                          </span>
                        </li>
                        <li className="flex items-start space-x-2" data-id="element-9480">
                          <span className="text-[#21A366] font-bold" data-id="element-9481">✓</span>
                          <span data-id="element-9482">
                            Vei putea edita orice date înainte de semnare
                          </span>
                        </li>
                        <li className="flex items-start space-x-2" data-id="element-9483">
                          <span className="text-[#21A366] font-bold" data-id="element-9484">✓</span>
                          <span data-id="element-9485">
                            Vei alege varianta de semnare pentru beneficiar
                          </span>
                        </li>
                        <li className="flex items-start space-x-2" data-id="element-9486">
                          <span className="text-[#21A366] font-bold" data-id="element-9487">✓</span>
                          <span data-id="element-9488">
                            Contractul va fi salvat în lista de contracte
                          </span>
                        </li>
                      </ul>
                    </div>}
                </div>}

              {/* STEP 7 - Client Signature */}
              {currentStep === 7 && generatedContract && <div className="space-y-6" data-id="element-9489">
                  <h3 className="text-2xl font-bold text-[#132C53] mb-6" data-id="element-9490">
                    Semnare beneficiar
                  </h3>

                  {!signingMode && <>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6" data-id="element-9491">
                        <p className="text-sm text-gray-700" data-id="element-9492">
                          <strong data-id="element-9493">Alege varianta de semnare:</strong>{' '}
                          Beneficiarul poate semna fizic (descarcă PDF) sau
                          digital (primește link pe WhatsApp).
                        </p>
                      </div>

                      <div className="space-y-4" data-id="element-9494">
                        <button type="button" onClick={() => setSigningMode('physical')} className="w-full p-6 bg-gradient-to-br from-gray-600 to-gray-700 text-white rounded-xl hover:shadow-lg transition-all text-left" data-id="element-9495">
                          <div className="flex items-center space-x-4" data-id="element-9496">
                            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center" data-id="element-9497">
                              <DownloadIcon className="w-6 h-6" data-id="element-9498" />
                            </div>
                            <div className="flex-1" data-id="element-9499">
                              <div className="font-bold text-lg mb-1" data-id="element-9500">
                                Semnare fizică
                              </div>
                              <p className="text-sm text-white text-opacity-90" data-id="element-9501">
                                Descarcă contract pentru printare și semnare
                                fizică
                              </p>
                            </div>
                          </div>
                        </button>

                        <button type="button" onClick={() => setSigningMode('digital')} className="w-full p-6 bg-gradient-to-br from-[#357BEB] to-[#2868d8] text-white rounded-xl hover:shadow-lg transition-all text-left" data-id="element-9502">
                          <div className="flex items-center space-x-4" data-id="element-9503">
                            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center" data-id="element-9504">
                              <SendIcon className="w-6 h-6" data-id="element-9505" />
                            </div>
                            <div className="flex-1" data-id="element-9506">
                              <div className="font-bold text-lg mb-1" data-id="element-9507">
                                Semnare digitală
                              </div>
                              <p className="text-sm text-white text-opacity-90" data-id="element-9508">
                                Trimite link pe WhatsApp pentru semnare la
                                distanță
                              </p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </>}

                  {signingMode === 'physical' && <>
                      <button type="button" onClick={() => setSigningMode(null)} className="text-sm text-gray-600 hover:text-gray-900 mb-4 flex items-center space-x-1" data-id="element-9509">
                        <ArrowLeftIcon className="w-4 h-4" data-id="element-9510" />
                        <span data-id="element-9511">Înapoi la opțiuni</span>
                      </button>

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-id="element-9512">
                        <p className="text-sm text-gray-700" data-id="element-9513">
                          Contractul va fi deschis în modul de printare. Doar
                          textul contractului va fi vizibil pentru o
                          previzualizare profesională și curată.
                        </p>
                      </div>

                      <div className="space-y-3" data-id="element-9514">
                        <button type="button" onClick={handleDownloadPDF} className="w-full flex items-center justify-center space-x-2 bg-gray-700 text-white px-6 py-4 rounded-lg font-bold hover:bg-gray-800 transition-colors" data-id="element-9515">
                          <PrinterIcon className="w-5 h-5" data-id="element-9516" />
                          <span data-id="element-9517">Printează contract</span>
                        </button>

                        <button type="button" onClick={handlePhysicalSigning} className="w-full flex items-center justify-center space-x-2 bg-[#21A366] text-white px-6 py-4 rounded-lg font-bold hover:bg-[#1a8555] transition-colors" data-id="element-9518">
                          <DownloadIcon className="w-5 h-5" data-id="element-9519" />
                          <span data-id="element-9520">Descarcă PDF</span>
                        </button>
                      </div>
                    </>}

                  {signingMode === 'digital' && <>
                      <button type="button" onClick={() => setSigningMode(null)} className="text-sm text-gray-600 hover:text-gray-900 mb-4 flex items-center space-x-1" data-id="element-9521">
                        <ArrowLeftIcon className="w-4 h-4" data-id="element-9522" />
                        <span data-id="element-9523">Înapoi la opțiuni</span>
                      </button>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-id="element-9524">
                        <p className="text-sm text-gray-700" data-id="element-9525">
                          Trimite link unic de semnare beneficiarului pe
                          WhatsApp la numărul:{' '}
                          <strong data-id="element-9526">{generatedContract.clientPhone}</strong>
                        </p>
                      </div>

                      <button type="button" onClick={handleSendWhatsApp} className="w-full flex items-center justify-center space-x-2 bg-[#25D366] text-white px-6 py-4 rounded-lg font-bold hover:bg-[#20BA5A] transition-colors" data-id="element-9527">
                        <SendIcon className="w-5 h-5" data-id="element-9528" />
                        <span data-id="element-9529">Trimite pe WhatsApp</span>
                      </button>

                      {remoteLink && <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-9530">
                          <p className="text-xs text-gray-600 mb-2" data-id="element-9531">
                            Link generat:
                          </p>
                          <p className="text-xs font-mono text-gray-800 break-all" data-id="element-9532">
                            {remoteLink}
                          </p>
                        </div>}
                    </>}
                </div>}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          {currentStep < 7 && <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200" data-id="element-9533">
              <button type="button" onClick={prevStep} disabled={currentStep === 1} className="flex items-center space-x-2 px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9534">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-9535" />
                <span data-id="element-9536">Înapoi</span>
              </button>

              {isEditMode && currentStep !== 6 ? <button type="button" onClick={handleSaveEdits} disabled={!canGoNext()} className="flex items-center space-x-2 px-8 py-3 bg-[#21A366] text-white rounded-lg font-semibold hover:bg-[#1a8555] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9537">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-9538" />
                  <span data-id="element-9539">Salvează Modificările</span>
                </button> : currentStep === 5 ? <button type="button" onClick={nextStep} disabled={!agentSignature} className="flex items-center space-x-2 px-8 py-3 bg-[#21A366] text-white rounded-lg font-semibold hover:bg-[#1a8555] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9540">
                  <span data-id="element-9541">Salvează semnătura</span>
                  <ChevronRightIcon className="w-5 h-5" data-id="element-9542" />
                </button> : currentStep === 6 && !generatedContract ? <button type="button" onClick={handleGenerateContract} className="flex items-center space-x-2 px-8 py-3 bg-[#21A366] text-white rounded-lg font-semibold hover:bg-[#1a8555] transition-colors" data-id="element-9543">
                  <FileTextIcon className="w-5 h-5" data-id="element-9544" />
                  <span data-id="element-9545">Generează contractul</span>
                </button> : currentStep === 6 && generatedContract ? <button type="button" onClick={nextStep} className="flex items-center space-x-2 px-8 py-3 bg-[#357BEB] text-white rounded-lg font-semibold hover:bg-[#2868d8] transition-colors" data-id="element-9546">
                  <span data-id="element-9547">Continuă la Semnare</span>
                  <ChevronRightIcon className="w-5 h-5" data-id="element-9548" />
                </button> : <button type="button" onClick={nextStep} disabled={!canGoNext()} className="flex items-center space-x-2 px-8 py-3 bg-[#357BEB] text-white rounded-lg font-semibold hover:bg-[#2868d8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9549">
                  <span data-id="element-9550">Continuă</span>
                  <ChevronRightIcon className="w-5 h-5" data-id="element-9551" />
                </button>}
            </div>}
        </div>
      </div>

      {/* Contract Preview Modal */}
      <ContractPreviewModal contract={generatedContract || {
      ...formData,
      // Add missing fields for preview if contract not generated yet
      createdAt: new Date().toISOString(),
      type: formData.clientRole === 'proprietar' ? 'landlord' : 'tenant',
      clientName: formData.clientType === 'PF' ? formData.fullName : formData.companyName,
      clientPhone: formData.clientType === 'PF' ? formData.phone : formData.companyPhone,
      clientEmail: formData.clientType === 'PF' ? formData.email : formData.companyEmail,
      clientCNP: formData.clientType === 'PF' ? formData.cnp : undefined,
      clientCI: formData.clientType === 'PF' ? `${formData.idSeries} ${formData.idNumber}` : undefined,
      clientAddress: formData.clientType === 'PF' ? formData.address : undefined,
      clientCUI: formData.clientType === 'PJ' ? formData.cui : undefined,
      clientCompanyName: formData.clientType === 'PJ' ? formData.companyName : undefined,
      clientCompanyAddress: formData.clientType === 'PJ' ? formData.companySediu : undefined,
      clientRegCom: formData.clientType === 'PJ' ? formData.regCom : undefined,
      clientLegalRep: formData.clientType === 'PJ' ? formData.legalRep : undefined,
      vatIncluded: formData.vatOption === 'vat_included'
    }} isOpen={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} title={generatedContract ? 'Contract Generat' : 'Previzualizare Draft'} data-id="element-9552" />
    </div>;
}